import axios from 'axios';
import { APIAdapterFactory } from './api-adapters';
import type {
  VideoConfig,
  GenerationResponse,
  ApiResponse,
  AudioGenerationRequest,
  AudioGenerationResponse,
} from '../types';
import { getApiBaseUrl } from './urlResolver';
import { isE2ETestMode } from '../utils/apiMode';

import { handleError } from '../utils/errorHandler';

/**
 * Simple axios instance for direct API calls
 * Uses the serverless API URL from environment variables
 * In desktop mode, uses localhost.
 * E2E bypass header is set ONCE at module load (matches Playwright flow:
 * flag is set before the FE bundle initializes).
 */
export const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(isE2ETestMode() ? { 'X-E2E-Test': 'true' } : {}),
  },
});

/**
 * Unified API Service class that uses the adapter pattern
 * Automatically selects between Flask local and Serverless APIs
 */
export class VideoGenerationAPI {
  /**
   * Generate video from configuration
   */
  static async generateVideo(config: VideoConfig): Promise<GenerationResponse> {
    const adapter = await APIAdapterFactory.getAdapter();
    return adapter.generateVideo(config);
  }

  /**
   * Check if backend service is available
   */
  static async checkHealth(): Promise<ApiResponse<{ status: string; version?: string }>> {
    const adapter = await APIAdapterFactory.getAdapter();
    return adapter.checkHealth();
  }

  /**
   * Get generation status
   */
  static async getGenerationStatus(
    generationId: string
  ): Promise<ApiResponse<Record<string, unknown>>> {
    const adapter = await APIAdapterFactory.getAdapter();
    return adapter.getGenerationStatus(generationId);
  }

  /**
   * Get generation progress (if supported by backend)
   */
  static async getGenerationProgress(
    jobId: string
  ): Promise<ApiResponse<import('../types').GenerationStatusResponse>> {
    const adapter = await APIAdapterFactory.getAdapter();
    const response = await adapter.getGenerationStatus(jobId);
    // Type assertion is safe here - API contract ensures GenerationStatusResponse structure
    return response as unknown as ApiResponse<import('../types').GenerationStatusResponse>;
  }

  /**
   * Force a specific API mode (useful for testing)
   * @param mode - 'local' for Flask, 'serverless' for Cloud Run/RunPod
   */
  static async setAPIMode(mode: 'local' | 'serverless'): Promise<void> {
    APIAdapterFactory.reset();
    await APIAdapterFactory.getAdapter(mode);
  }

  /**
   * Get the current API mode being used
   */
  static getCurrentAPIMode(): string {
    return APIAdapterFactory.getCurrentMode() || 'not initialized';
  }

  /**
   * Get the download URL for a specific job
   * Note: Video URLs are now returned directly in job status/results
   */
  static async getDownloadUrl(jobId: string): Promise<string> {
    const statusResponse = await this.getGenerationStatus(jobId);
    if (statusResponse.success && statusResponse.data) {
      const data = statusResponse.data as Record<string, unknown>;
      return (data.result_url as string) || '';
    }
    return '';
  }

  /**
   * Get the streaming URL for a specific job
   * Note: Now same as download URL since we use direct presigned URLs
   */
  static async getStreamingUrl(jobId: string): Promise<string> {
    return this.getDownloadUrl(jobId);
  }

  /**
   * Get the base URL for the current API adapter
   * Useful for SSE connections and other direct API calls
   */
  static async getBaseUrl(): Promise<string> {
    const adapter = await APIAdapterFactory.getAdapter();
    return (adapter as unknown as { baseURL: string }).baseURL;
  }

  /**
   * Generate audio from configuration
   */
  static async generateAudio(config: AudioGenerationRequest): Promise<AudioGenerationResponse> {
    try {
      // Use adapter pattern for consistency with video generation
      const adapter = await APIAdapterFactory.getAdapter();
      return adapter.generateAudio(config);
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Api', 'Failed to generate audio');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get audio generation status
   */
  static async getAudioStatus(jobId: string): Promise<ApiResponse<AudioGenerationResponse>> {
    try {
      // Use adapter pattern for consistency
      const adapter = await APIAdapterFactory.getAdapter();
      return adapter.getAudioStatus(jobId) as unknown as Promise<
        ApiResponse<AudioGenerationResponse>
      >;
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Api', 'Failed to get audio status');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}
